import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { PostmarkSuccess, Field } from "./FormBits";
import { startCheckout } from "../../lib/payments";
import { SPONSORSHIP_PACKAGE_URL } from "../../lib/data";
import { FileText } from "lucide-react";

const TIERS = [
  { key: "Community $300", label: "Community — $300" },
  { key: "Champion $500", label: "Champion — $500" },
  { key: "Hero $1,000", label: "Hero — $1,000" },
  { key: "Title Sponsor $5,000+", label: "Title Sponsor — $5,000+" },
  { key: "Custom", label: "Custom" },
];

export const SponsorshipForm = () => {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", tier: "", message: "" });
  const [logoPath, setLogoPath] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cardEligible = form.tier && form.tier !== "Custom";

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(r.data.path);
      setLogoName(file.name);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tier) return toast.error("Please choose a sponsorship tier");
    setBusy(true);
    try {
      const r = await api.post("/forms/sponsorship", { ...form, logo_path: logoPath });
      if (cardEligible) {
        await startCheckout({ kind: "sponsorship", ref_id: r.data.id });
        return;
      }
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <PostmarkSuccess
        title="Thank You For Sponsoring!"
        message="Your custom sponsorship inquiry has reached the CPTCA team. We'll be in touch shortly to confirm the details and arrange payment. To discuss options directly, call 204-951-8164."
        onReset={() => { setForm({ company: "", contact_name: "", email: "", phone: "", tier: "", message: "" }); setLogoPath(null); setLogoName(""); setDone(false); }}
      />
    );

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6" data-testid="sponsorship-form">
      <a href={SPONSORSHIP_PACKAGE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 border-2 border-rust bg-cream-muted px-4 py-3 hover:bg-rust/10 transition-colors" data-testid="sp-package-link">
        <FileText size={20} className="text-rust shrink-0" />
        <span className="text-sm text-forest-light">View the full <strong className="text-rust">Pak Fest 2026 Sponsorship Package (PDF)</strong> — tiers, benefits &amp; reach.</span>
      </a>
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Company / Organization" required>
          <input className="vintage-input" required value={form.company} onChange={set("company")} data-testid="sp-company" />
        </Field>
        <Field label="Contact Name" required>
          <input className="vintage-input" required value={form.contact_name} onChange={set("contact_name")} data-testid="sp-contact" />
        </Field>
        <Field label="Email" required>
          <input type="email" className="vintage-input" required value={form.email} onChange={set("email")} data-testid="sp-email" />
        </Field>
        <Field label="Phone" required>
          <input className="vintage-input" required value={form.phone} onChange={set("phone")} data-testid="sp-phone" />
        </Field>
      </div>

      <Field label="Sponsorship Tier" required>
        <select className="vintage-input" required value={form.tier} onChange={set("tier")} data-testid="sp-tier">
          <option value="">Select a tier…</option>
          {TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </Field>

      <Field label="Message">
        <textarea className="vintage-input min-h-[90px] resize-y" value={form.message} onChange={set("message")} placeholder="Tell us how you'd like to support Pak Fest…" data-testid="sp-message" />
      </Field>

      <Field label="Upload Your Logo (for the Sponsors page)">
        <label className="flex items-center gap-3 border-2 border-dashed border-forest px-4 py-3 cursor-pointer hover:bg-cream-muted transition-colors" data-testid="sp-logo-label">
          {uploading ? <Loader2 size={20} className="animate-spin text-rust" /> : <Upload size={20} className="text-rust" />}
          <span className="font-mono text-sm text-forest-light">{logoName || "Choose an image file (PNG/JPG)"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="sp-logo-input" />
        </label>
      </Field>

      <div className="border-2 border-dashed border-forest/50 p-3 text-sm text-forest-light" data-testid="sp-paynote">
        {cardEligible
          ? "Payment is by secure card (Stripe) at submission. Sponsorship benefits are confirmed once payment is received."
          : "Choose a fixed tier to pay now by card, or pick Custom and we'll follow up to arrange your sponsorship."}
        <br />Payment issues or want an alternative like e-Transfer? Call <span className="font-bold text-rust">204-951-8164</span>.
      </div>

      <button type="submit" disabled={busy || uploading} className="btn-rust w-full py-4 text-base disabled:opacity-60" data-testid="sp-submit">
        {busy ? "Submitting…" : cardEligible ? "Submit & Pay by Card" : "Submit Custom Inquiry"}
      </button>
    </form>
  );
};
